/**
 * Canonical values for the Analytics (alert) configuration UI.
 *
 * `value` is what gets stored in alert_configs.event_type and what the command-center's
 * alertEvaluator matches against — i.e. the event's canonical type (classifyEvent uses the
 * sub_type when present, e.g. a "person/crossing" event matches `crossing`). `'*'` matches
 * any event type. The field is a free-typeable datalist, so an AI module emitting a type
 * not listed here can still be targeted by typing it in.
 */
export interface Option {
  value: string;
  label: string;
}

/**
 * The model-level keys, listed FIRST because they are the ones that keep working.
 *
 * Everything below them is the AI module's own spelling for an event, and that
 * changes between model versions — silently. A rule targeting 'fire_detected'
 * matched nothing the day the fire model started emitting 'fire_alert', and a
 * fire raised no alert while the rule sat there looking perfectly correct.
 * These keys are derived from the event's source table by the command-center's
 * classifier, so a rule written against one survives the model being renamed.
 */
export const EVENT_TYPE_OPTIONS: Option[] = [
  { value: '*', label: 'All event types' },
  { value: 'person', label: 'Person — any (model key)' },
  { value: 'person_count', label: 'Person count — any (model key)' },
  { value: 'intrusion', label: 'Intrusion — any (model key)' },
  { value: 'face', label: 'Face recognition — any (model key)' },
  { value: 'vehicle_crossing', label: 'Vehicle detection — any (model key)' },
  { value: 'ppe_violation', label: 'PPE violation — any (model key)' },
  { value: 'theft', label: 'Theft — any (model key)' },
  { value: 'fall', label: 'Fall — any (model key)' },
  { value: 'fire_smoke', label: 'Fire & smoke — any (model key)' },
  { value: 'crossing', label: 'Person crossing' },
  { value: 'loitering', label: 'Person loitering' },
  { value: 'person_counting', label: 'Person counting' },
  { value: 'zone_breach', label: 'Intrusion (zone breach)' },
  { value: 'face_match', label: 'Face match (known)' },
  { value: 'unknown_face', label: 'Unknown face' },
  { value: 'anpr_inout_match', label: 'Vehicle detection (ANPR)' },
  // (no raw 'ppe_violation' entry: that model's key is the same string, listed above)
  { value: 'shoplifting', label: 'Theft / shoplifting' },
  { value: 'fall_detected', label: 'Fall detected' },
  // 'fire_detected' is what migration 022 seeds as the shipped Fire / smoke rule
  // and is the value an existing install already has stored, so it stays the one
  // offered here. 'smoke_detected' is separate because a site may well want a
  // quieter rule for smoke than for flame — both route to the same report.
  { value: 'fire_detected', label: 'Fire detected' },
  { value: 'smoke_detected', label: 'Smoke detected' },
  // What the deployed fire model actually emits. Listed alongside the two above
  // rather than instead of them: which spelling arrives depends on the model
  // version, and a rule targeting the wrong one silently never matches — the
  // event then falls to the '*' catch-all and an operator sees a fire reported
  // under the name "All other detections".
  { value: 'fire_alert', label: 'Fire alert (fire_alert)' },
  { value: 'smoke_alert', label: 'Smoke alert (smoke_alert)' },
];

export const SEVERITY_OPTIONS: Option[] = [
  { value: '*', label: 'Auto (rule decides)' },
  { value: 'critical', label: 'Critical' },
  { value: 'warning', label: 'Warning' },
  { value: 'normal', label: 'Normal / info' },
];

/** Scope of a rule: all cameras, a single camera, a whole group, or a hand-picked list of cameras. */
export type RuleScope = 'all' | 'camera' | 'group' | 'cameras';

/** Derive the scope of an existing config row from its camera_id/group_id. */
export function scopeOf(cfg: { camera_id?: string | null; group_id?: string | null; camera_ids?: string[] | null }): RuleScope {
  // A list (even an empty one, while cameras are being picked) is "some cameras".
  if (Array.isArray(cfg.camera_ids)) return 'cameras';
  if (cfg.camera_id) return 'camera';
  if (cfg.group_id) return 'group';
  return 'all';
}

/** Split a stored event_type (comma-separated = multiple types) into its tokens. */
export const parseEventTypes = (value?: string | null): string[] =>
  String(value ?? '').split(',').map((t) => t.trim()).filter(Boolean);

/** Human label for a stored event_type — handles single, '*', and comma-separated lists. */
export const eventTypeLabel = (value?: string | null): string => {
  const tokens = parseEventTypes(value);
  if (tokens.length === 0) return 'All event types';
  return tokens.map((t) => EVENT_TYPE_OPTIONS.find((o) => o.value === t)?.label || t).join(', ');
};
